/* Manifesto do Destinatario — NF de Entrada.
 * Lista DF-e emitidos contra o CNPJ do tenant + acoes de manifestacao
 * (Ciencia / Confirmacao / Desconhecimento / Nao Realizada) + opcao de
 * "Dar entrada no sistema" (cria uma Invoice draft de tipo avulsa).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import {
  RefreshCw, Eye, ThumbsUp, ThumbsDown, AlertOctagon, Download as DownloadIcon, ArrowDownToLine, Search, Link2, Unlink, ShoppingCart, ChevronDown, Info, KeyRound, History,
} from 'lucide-react';
import { confirmDialog } from '../components/ConfirmDialog';
import { ManifestoFetchChaveModal, runManifestoSync } from '../components/ManifestoFetchChave';

const ERP = `${API}/erp`;
const inputClass = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-500/20";

const STATUS_META = {
  pendente: { label: 'Pendente', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  ciencia: { label: 'Ciencia', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  confirmada: { label: 'Confirmada', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  desconhecida: { label: 'Desconhecida', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  nao_realizada: { label: 'Nao Realizada', color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const fmtBRL = (n) => `R$ ${(Number(n) || 0).toFixed(2).replace('.', ',')}`;
const fmtCnpj = (d) => { const s = String(d || '').replace(/\D/g, ''); return s.length === 14 ? s.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : s; };
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return iso.slice(0, 10); }
};

export const ManifestoPage = ({ companyId }) => {
  const [docs, setDocs] = useState([]);
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(false);   // iter156fh: "Como funciona"
  const goToPurchase = (doc) => {
    const slug = window.location.pathname.split('/').filter(Boolean)[0] || '';
    window.location.href = `/${slug}/purchases?manifesto_doc=${doc.id}`;
  };
  const openLinkedPurchase = (doc) => {
    const slug = window.location.pathname.split('/').filter(Boolean)[0] || '';
    window.location.href = `/${slug}/purchases?open=${doc.linked_purchase.id}`;
  };
  const [syncing, setSyncing] = useState(false);
  const [fetchChaveOpen, setFetchChaveOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [eventModal, setEventModal] = useState(null); // { doc, tipo }
  const [justificativa, setJustificativa] = useState('');
  const [sending, setSending] = useState(false);
  // iter156bo: modal de vinculo com NF existente
  const [linkModal, setLinkModal] = useState(null); // { doc }
  const [linkSearch, setLinkSearch] = useState('');
  const [linkInvoices, setLinkInvoices] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkSelected, setLinkSelected] = useState(null); // invoice_id
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (search) params.set('q', search);
      if (companyId) params.set('company_id', companyId);   // iter156fh: so notas contra o CNPJ desta filial
      const { data } = await axios.get(`${ERP}/manifesto?${params.toString()}`, { withCredentials: true });
      setDocs(data.data || []);
      setState(data.state || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar manifesto');
    } finally { setLoading(false); }
  }, [filterStatus, search, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    return handleSyncInner(false);
  };

  const handleSyncInner = async (fullRebuild) => {
    // iter156bn: SEFAZ 656 "Consumo Indevido" — quando uLtNSU == maxNSU (ja atualizado)
    // e a ultima consulta foi ha menos de 60 minutos, bloqueamos client-side com um
    // toast amigavel. Nao aplica quando o usuario forcou fullRebuild.
    if (!fullRebuild && state.ult_nsu && state.max_nsu && state.ult_nsu === state.max_nsu && state.last_sync_at) {
      const elapsedMin = (Date.now() - new Date(state.last_sync_at).getTime()) / 60000;
      if (elapsedMin < 60) {
        const wait = Math.max(1, Math.ceil(60 - elapsedMin));
        toast.info(
          `Ja esta atualizado (uLtNSU = maxNSU = ${state.ult_nsu}). A SEFAZ exige aguardar 60 minutos entre consultas quando nao ha DF-e novo. Proxima consulta em ~${wait} min.`,
          { duration: 10000 }
        );
        return;
      }
    }
    setSyncing(true);
    try {
      await runManifestoSync(companyId, { reset: fullRebuild });
      load();
    } finally { setSyncing(false); }
  };

  const handleFullRebuild = async () => {
    const ok = await confirmDialog({
      title: 'Rebuscar TODO o historico na SEFAZ?\n\nReinicia o NSU para 0 e baixa novamente todas as notas disponiveis na SEFAZ (ate 90 dias). Notas ja manifestadas/importadas sao preservadas. Use quando alguma nota existente nao apareceu.',
      confirmLabel: 'Sim, rebuscar tudo',
    });
    if (ok) handleSyncInner(true);
  };

  // iter156bn: helpers para o banner de status — indica cooldown SEFAZ (656) de forma amigavel
  const isConsumoIndevido = (state.last_cstat === '656') || ((state.last_xmotivo || '').toLowerCase().includes('consumo indevido'));
  const cooldownMinLeft = React.useMemo(() => {
    if (!state.last_sync_at) return 0;
    const elapsedMin = (Date.now() - new Date(state.last_sync_at).getTime()) / 60000;
    return Math.max(0, Math.ceil(60 - elapsedMin));
  }, [state.last_sync_at]);

  const openEvent = (doc, tipo) => {
    setEventModal({ doc, tipo });
    setJustificativa('');
  };

  const sendEvent = async () => {
    if (!eventModal) return;
    const { doc, tipo } = eventModal;
    if (tipo === '210240' && !justificativa.trim()) {
      toast.error('Justificativa obrigatoria (min 15 chars)');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${ERP}/manifesto/${doc.id}/event`, {
        tipo_evento: tipo,
        justificativa: justificativa,
      }, { withCredentials: true, timeout: 60000 });
      if (data.last_event?.cstat === '135' || data.last_event?.cstat === '136') {
        toast.success(`Evento registrado (cstat ${data.last_event.cstat})`);
      } else {
        toast.error(`SEFAZ ${data.last_event?.cstat}: ${(data.last_event?.xmotivo || '').slice(0, 150)}`, { duration: 8000 });
      }
      setEventModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar evento', { duration: 8000 });
    } finally { setSending(false); }
  };

  const handleImport = async (doc) => {
    if (!companyId) { toast.error('Selecione uma Empresa antes de importar'); return; }
    if (!(await confirmDialog({ title: `Dar entrada no sistema?\n\nEmitente: ${doc.emitente_nome}\nValor: ${fmtBRL(doc.valor_nf)}\n\nSera criada uma Invoice rascunho tipo 'avulsa' que voce pode editar para adicionar itens reais e gerar movimento de estoque.`, confirmLabel: 'Confirmar', danger: false }))) return;
    try {
      const { data } = await axios.post(`${ERP}/manifesto/${doc.id}/import`, { company_id: companyId }, { withCredentials: true });
      toast.success(`Entrada criada (NF #${data.numero}). Edite em Notas Fiscais.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar');
    }
  };

  // iter156bo: abrir modal de vinculo com NF ja lancada
  const openLink = (doc) => {
    setLinkModal({ doc });
    setLinkSearch('');
    setLinkSelected(null);
    setLinkInvoices([]);
    // Pre-busca automatica: prioriza NFs da empresa atual pelo CNPJ do emitente
    fetchInvoices(doc.emitente_cnpj || '');
  };

  const fetchInvoices = async (q) => {
    if (!companyId) return;
    setLinkLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('company_id', companyId);
      params.set('limit', '100');
      if (q) params.set('q', q);
      const { data } = await axios.get(`${ERP}/invoices?${params.toString()}`, { withCredentials: true });
      setLinkInvoices(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao buscar NFs');
    } finally { setLinkLoading(false); }
  };

  // Debounce simples na busca
  useEffect(() => {
    if (!linkModal) return;
    const t = setTimeout(() => { fetchInvoices(linkSearch); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkSearch, linkModal]);

  const doLink = async () => {
    if (!linkModal || !linkSelected) return;
    setLinking(true);
    try {
      const { data } = await axios.post(
        `${ERP}/manifesto/${linkModal.doc.id}/link`,
        { invoice_id: linkSelected },
        { withCredentials: true }
      );
      toast.success(`DF-e vinculado a NF #${data.numero || '?'}`);
      setLinkModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao vincular');
    } finally { setLinking(false); }
  };

  const doUnlink = async (doc) => {
    if (!(await confirmDialog({ title: 'Desvincular este DF-e da NF?', confirmLabel: 'Desvincular', danger: true }))) return;
    try {
      await axios.post(`${ERP}/manifesto/${doc.id}/unlink`, {}, { withCredentials: true });
      toast.success('Vinculo removido');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao desvincular');
    }
  };

  return (
    <div className="space-y-4" data-testid="manifesto-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-slate-900 flex items-center gap-2">
            <ArrowDownToLine className="text-violet-600" /> NF de Entrada (Manifesto)
          </h2>
          <p className="text-xs text-slate-500">
            Notas Fiscais emitidas contra o <b>CNPJ da filial selecionada</b> (certificado e NSU proprios por filial). Historico padrao: 30 dias.
            <button type="button" onClick={() => setShowHelp(v => !v)} className="ml-2 inline-flex items-center gap-1 text-violet-700 font-bold hover:underline" data-testid="manifesto-help-toggle"><Info size={12} /> Como funciona <ChevronDown size={12} className={`transition-transform ${showHelp ? 'rotate-180' : ''}`} /></button>
          </p>
          {showHelp && (
            <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50/60 p-4 text-xs text-slate-700 space-y-2 max-w-3xl" data-testid="manifesto-help-panel">
              <p className="font-black text-violet-800 uppercase tracking-wider text-[10px]">Passo a passo da nota de entrada</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li><b>Sincronizar SEFAZ</b>: busca as notas emitidas contra o CNPJ desta filial usando o certificado dela.</li>
                <li><b>Manifestar</b> (opcional, mas recomendado): 👁 Ciencia → 👍 Confirmacao (ou 👎 Desconhecimento / ❗ Nao realizada). A Ciencia libera o XML completo.</li>
                <li><b>Dar entrada</b> escolhendo UM dos caminhos:</li>
              </ol>
              <div className="grid sm:grid-cols-2 gap-2 ml-4">
                <div className="rounded-lg bg-white border border-emerald-200 p-2.5"><p className="font-bold text-emerald-700 flex items-center gap-1"><ShoppingCart size={12} /> Dar entrada no Compras</p><p className="text-[11px] text-slate-600 mt-1">Abre a compra ja preenchida com fornecedor e itens. Ao finalizar: alimenta <b>Fiscal + Estoque (desta filial) + Financeiro (contas a pagar)</b>. A nota fica marcada como <b>Importada</b> aqui.</p></div>
                <div className="rounded-lg bg-white border border-slate-200 p-2.5"><p className="font-bold text-slate-700 flex items-center gap-1"><DownloadIcon size={12} /> Importar como NF avulsa</p><p className="text-[11px] text-slate-600 mt-1">Registra a nota apenas no <b>Fiscal</b> (escrituracao). <b>Nao</b> movimenta estoque nem gera contas a pagar.</p></div>
              </div>
              <p className="text-[11px] text-slate-500 ml-4">🔗 Vincular: associa a nota a uma NF ja lancada manualmente. O status colorido (Pendente/Ciencia/Confirmada) e o status do <i>manifesto na SEFAZ</i>; "Importada" indica que ja virou compra ou NF avulsa no sistema.</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setFetchChaveOpen(true)} className="px-3 py-2.5 rounded-xl font-bold text-sm border border-violet-300 text-violet-700 bg-white hover:bg-violet-50 flex items-center gap-2" data-testid="manifesto-fetch-chave-btn" title="Consultar uma nota especifica pela chave de acesso">
            <KeyRound size={15} /> Buscar pela chave
          </button>
          <button onClick={handleFullRebuild} disabled={syncing} className="px-3 py-2.5 rounded-xl font-bold text-sm border border-slate-300 text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2" data-testid="manifesto-full-rebuild-btn" title="Reinicia o NSU (0) e baixa novamente todo o historico disponivel">
            <History size={15} /> Rebuscar tudo
          </button>
          <button
            onClick={handleSync}
            disabled={syncing}
            className={`px-4 py-2.5 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm shadow-md flex items-center gap-2 ${
              isConsumoIndevido && cooldownMinLeft > 0
                ? 'bg-slate-400 hover:bg-slate-500 shadow-slate-400/30'
                : 'bg-violet-600 hover:bg-violet-700 shadow-violet-500/30'
            }`}
            data-testid="manifesto-sync-btn"
            title={isConsumoIndevido && cooldownMinLeft > 0 ? `Aguarde ${cooldownMinLeft} min (protecao SEFAZ)` : 'Consultar SEFAZ'}
          >
            {syncing ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <RefreshCw size={16} />}
            {isConsumoIndevido && cooldownMinLeft > 0 ? `Aguarde ~${cooldownMinLeft} min` : 'Sincronizar SEFAZ'}
          </button>
        </div>
      </div>

      {/* Estado da Distribuicao */}
      <div className="bg-violet-50/40 border border-violet-200 rounded-2xl p-3 text-xs text-slate-700 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="manifesto-state">
        <div><span className="font-bold">uLtNSU:</span> <span className="font-mono">{state.ult_nsu || 0}</span></div>
        <div><span className="font-bold">maxNSU:</span> <span className="font-mono">{state.max_nsu || 0}</span></div>
        <div><span className="font-bold">Ultimo sync:</span> {state.last_sync_at ? new Date(state.last_sync_at).toLocaleString('pt-BR') : '—'}</div>
        <div><span className="font-bold">SEFAZ:</span> {state.last_cstat ? `${state.last_cstat} ${(state.last_xmotivo || '').slice(0, 30)}` : '—'}</div>
        {state.cnpj && (
          <div className="col-span-2 md:col-span-4 pt-1.5 mt-0.5 border-t border-violet-200/70 flex flex-wrap gap-x-4 gap-y-1" data-testid="manifesto-state-diag">
            <span><span className="font-bold">CNPJ consultado:</span> <span className="font-mono">{fmtCnpj(state.cnpj)}</span></span>
            {state.cert_cnpj && <span><span className="font-bold">Certificado:</span> <span className={`font-mono ${state.cert_cnpj !== state.cnpj ? 'text-amber-700 font-bold' : ''}`}>{fmtCnpj(state.cert_cnpj)}</span></span>}
            {state.ambiente && <span><span className="font-bold">Ambiente:</span> <span className={state.ambiente === 'producao' ? 'text-emerald-700 font-bold' : 'text-amber-700 font-bold'}>{state.ambiente === 'producao' ? 'Producao' : 'Homologacao'}</span></span>}
            {state.uf && <span><span className="font-bold">UF autor:</span> {state.uf}</span>}
          </div>
        )}
      </div>

      {/* iter156bn: Banner informativo — SEFAZ 656 "Consumo Indevido" (nao e erro de config) */}
      {isConsumoIndevido && cooldownMinLeft > 0 && (
        <div className="bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-2xl p-4 flex items-start gap-3" data-testid="manifesto-cooldown-banner">
          <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
            <RefreshCw className="text-sky-600" size={18} />
          </div>
          <div className="flex-1 text-sm">
            <p className="font-black text-sky-900">Manifesto atualizado — aguardando janela SEFAZ</p>
            <p className="text-xs text-sky-800/80 mt-1">
              O SEFAZ retornou <b>656 (Consumo Indevido)</b> porque nao ha DF-e novo (uLtNSU = maxNSU = <span className="font-mono">{state.ult_nsu}</span>). E uma protecao anti-abuso do webservice — nao ha problema com o cadastro da empresa (CNPJ, Inscricao Estadual e Certificado A1 estao OK).
            </p>
            <p className="text-xs text-sky-800 mt-2">
              Proxima consulta permitida em <b className="tabular-nums">~{cooldownMinLeft} min</b>. Enquanto isso, use os filtros para consultar os DF-e ja recebidos.
            </p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 shadow-sm">
        <div className="relative md:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por emitente, CNPJ ou chave..."
            className={`${inputClass} pl-9`}
            data-testid="manifesto-search"
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={inputClass} data-testid="manifesto-filter-status">
          <option value="">Todos os status</option>
          <option value="pendente">Pendente</option>
          <option value="ciencia">Ciencia</option>
          <option value="confirmada">Confirmada</option>
          <option value="desconhecida">Desconhecida</option>
          <option value="nao_realizada">Nao Realizada</option>
        </select>
      </div>

      {/* Lista */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-600 border-t-transparent" /></div>
        ) : docs.length === 0 ? (
          <div className="p-10 text-center" data-testid="manifesto-empty">
            <ArrowDownToLine size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">Nenhum DF-e encontrado.</p>
            <p className="text-xs text-slate-400 mt-1">Clique em &quot;Sincronizar SEFAZ&quot; para consultar notas emitidas contra o seu CNPJ. Tem o DANFE em maos e a nota nao apareceu? Use &quot;Buscar pela chave&quot;.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2.5 font-bold text-xs uppercase">Emitente</th>
                  <th className="text-left px-3 py-2.5 font-bold text-xs uppercase">Chave</th>
                  <th className="text-left px-3 py-2.5 font-bold text-xs uppercase">Emissao</th>
                  <th className="text-right px-3 py-2.5 font-bold text-xs uppercase">Valor</th>
                  <th className="text-center px-3 py-2.5 font-bold text-xs uppercase">Status</th>
                  <th className="text-right px-3 py-2.5 font-bold text-xs uppercase">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {docs.map(doc => {
                  const sm = STATUS_META[doc.manifesto_status] || STATUS_META.pendente;
                  return (
                    <tr key={doc.id} className="hover:bg-violet-50/40 transition-colors" data-testid={`manifesto-row-${doc.id}`}>
                      <td className="px-3 py-2.5">
                        <p className="font-bold text-slate-800 text-xs">{doc.emitente_nome || '—'}{doc.awaiting_full_xml && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-black uppercase" data-testid={`manifesto-awaiting-${doc.id}`}>Aguardando XML</span>}</p>
                        <p className="text-[10px] text-slate-400">CNPJ {doc.emitente_cnpj}</p>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[10px] text-slate-500">
                        {(doc.chave || '').replace(/(\d{4})(?=\d)/g, '$1 ')}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(doc.dhEmi)}</td>
                      <td className="px-3 py-2.5 text-right font-black text-slate-800">{fmtBRL(doc.valor_nf)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex items-center text-[10px] font-bold px-2 py-1 rounded-full border ${sm.color}`}>{sm.label}</span>
                        {doc.linked_purchase && (
                          <button onClick={() => openLinkedPurchase(doc)} className="block mx-auto text-[9px] text-emerald-700 font-bold mt-0.5 hover:underline" title="Abrir a compra gerada" data-testid={`manifesto-purchase-${doc.id}`}>
                            🛒 Importada no Compras {doc.linked_purchase.status === 'draft' ? '(rascunho)' : ''}
                          </button>
                        )}
                        {doc.imported_invoice_id && (
                          <span className="block text-[9px] text-emerald-600 font-bold mt-0.5" data-testid={`manifesto-linked-${doc.id}`}>
                            {doc.import_mode === 'linked'
                              ? `🔗 Vinculada${doc.linked_invoice?.numero ? ` NF #${doc.linked_invoice.numero}` : ''}`
                              : `✓ Importada${doc.linked_invoice?.numero ? ` NF #${doc.linked_invoice.numero}` : ''}`}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <button onClick={() => openEvent(doc, '210210')} title="Ciencia da Operacao (1o passo obrigatorio)" className="p-2 rounded-lg hover:bg-sky-100 text-sky-600" data-testid={`manifesto-ciencia-${doc.id}`}><Eye size={14} /></button>
                          <button onClick={() => openEvent(doc, '210200')} title="Confirmacao da Operacao" className="p-2 rounded-lg hover:bg-emerald-100 text-emerald-600" data-testid={`manifesto-confirmar-${doc.id}`}><ThumbsUp size={14} /></button>
                          <button onClick={() => openEvent(doc, '210220')} title="Desconhecimento da Operacao" className="p-2 rounded-lg hover:bg-rose-100 text-rose-600" data-testid={`manifesto-desconhecer-${doc.id}`}><ThumbsDown size={14} /></button>
                          <button onClick={() => openEvent(doc, '210240')} title="Operacao Nao Realizada (exige justificativa)" className="p-2 rounded-lg hover:bg-amber-100 text-amber-600" data-testid={`manifesto-nao-realizada-${doc.id}`}><AlertOctagon size={14} /></button>
                          {!doc.linked_purchase && !doc.imported_invoice_id && (
                            <button onClick={() => goToPurchase(doc)} title="Dar entrada no Compras (fiscal + estoque + financeiro)" className="p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700" data-testid={`manifesto-to-purchase-${doc.id}`}><ShoppingCart size={14} /></button>
                          )}
                          {!doc.imported_invoice_id && (
                            <>
                              <button onClick={() => openLink(doc)} title="Vincular a NF ja lancada no sistema" className="p-2 rounded-lg hover:bg-blue-100 text-blue-600" data-testid={`manifesto-link-${doc.id}`}><Link2 size={14} /></button>
                              <button onClick={() => handleImport(doc)} title="Dar entrada no sistema (cria Invoice rascunho)" className="p-2 rounded-lg hover:bg-violet-100 text-violet-600" data-testid={`manifesto-import-${doc.id}`}><DownloadIcon size={14} /></button>
                            </>
                          )}
                          {doc.imported_invoice_id && doc.import_mode === 'linked' && (
                            <button onClick={() => doUnlink(doc)} title="Desvincular DF-e da NF" className="p-2 rounded-lg hover:bg-rose-100 text-rose-600" data-testid={`manifesto-unlink-${doc.id}`}><Unlink size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Evento Modal */}
      {eventModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="manifesto-event-modal">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-violet-600 to-cyan-500 px-5 py-4 text-white">
              <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Manifestacao</p>
              <p className="text-xl font-black">
                {eventModal.tipo === '210200' && 'Confirmar Operacao'}
                {eventModal.tipo === '210210' && 'Dar Ciencia'}
                {eventModal.tipo === '210220' && 'Desconhecer Operacao'}
                {eventModal.tipo === '210240' && 'Operacao Nao Realizada'}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1">
                <p><span className="font-bold">Emitente:</span> {eventModal.doc.emitente_nome}</p>
                <p><span className="font-bold">CNPJ:</span> {eventModal.doc.emitente_cnpj}</p>
                <p><span className="font-bold">Valor:</span> {fmtBRL(eventModal.doc.valor_nf)}</p>
                <p className="font-mono text-[9px] text-slate-400">{eventModal.doc.chave}</p>
              </div>
              {eventModal.tipo === '210240' && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Justificativa <span className="text-rose-500">*</span></label>
                  <textarea
                    value={justificativa}
                    onChange={e => setJustificativa(e.target.value)}
                    rows={3}
                    minLength={15}
                    maxLength={255}
                    className={inputClass}
                    placeholder="Mercadoria nao recebida, divergencia de pedido, etc. (min 15, max 255 chars)"
                    data-testid="manifesto-justify"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">{justificativa.length}/255 chars</p>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setEventModal(null)} className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm font-bold" data-testid="manifesto-event-cancel">Cancelar</button>
                <button onClick={sendEvent} disabled={sending} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm flex items-center gap-2" data-testid="manifesto-event-confirm">
                  {sending && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                  Enviar ao SEFAZ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iter156bo: Modal de Vinculo com NF ja lancada */}
      {linkModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="manifesto-link-modal">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-5 py-4 text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Link2 size={18} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Vincular DF-e</p>
                <p className="text-xl font-black">Selecione a NF ja lancada</p>
              </div>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs grid grid-cols-1 md:grid-cols-3 gap-2">
                <div><span className="font-bold text-slate-500 uppercase text-[10px]">Emitente</span><p className="font-bold text-slate-800 truncate">{linkModal.doc.emitente_nome}</p></div>
                <div><span className="font-bold text-slate-500 uppercase text-[10px]">CNPJ</span><p className="font-mono text-slate-700">{linkModal.doc.emitente_cnpj}</p></div>
                <div><span className="font-bold text-slate-500 uppercase text-[10px]">Valor</span><p className="font-black text-emerald-700">{fmtBRL(linkModal.doc.valor_nf)}</p></div>
                <div className="md:col-span-3"><span className="font-bold text-slate-500 uppercase text-[10px]">Chave</span><p className="font-mono text-[10px] text-slate-500 break-all">{linkModal.doc.chave}</p></div>
              </div>

              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={linkSearch}
                  onChange={e => setLinkSearch(e.target.value)}
                  placeholder="Buscar por numero, cliente, CNPJ, natureza..."
                  className={`${inputClass} pl-9`}
                  data-testid="manifesto-link-search"
                  autoFocus
                />
              </div>

              {!companyId && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                  Selecione uma Empresa no topo do ERP para listar as notas fiscais.
                </div>
              )}

              <div className="border border-slate-200 rounded-xl overflow-hidden">
                {linkLoading ? (
                  <div className="p-6 flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent" /></div>
                ) : linkInvoices.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-500" data-testid="manifesto-link-empty">
                    Nenhuma NF encontrada. Ajuste a busca ou emita a nota antes de vincular.
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                    {linkInvoices.map(inv => {
                      const isSelected = linkSelected === inv.id;
                      const valueMatch = Math.abs((Number(inv.total) || 0) - (Number(linkModal.doc.valor_nf) || 0)) < 0.01;
                      const docMatch = (inv.customer_doc || '').replace(/\D/g, '') === (linkModal.doc.emitente_cnpj || '').replace(/\D/g, '');
                      const chaveMatch = inv.chave_acesso && inv.chave_acesso === linkModal.doc.chave;
                      const alreadyLinked = !!inv.manifesto_doc_id;
                      return (
                        <button
                          key={inv.id}
                          type="button"
                          onClick={() => !alreadyLinked && setLinkSelected(inv.id)}
                          disabled={alreadyLinked}
                          className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                            isSelected ? 'bg-blue-50 ring-2 ring-inset ring-blue-500' : alreadyLinked ? 'bg-slate-50 opacity-60 cursor-not-allowed' : 'hover:bg-slate-50'
                          }`}
                          data-testid={`manifesto-link-option-${inv.id}`}
                        >
                          <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${isSelected ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}>
                            {isSelected && <div className="w-full h-full rounded-full bg-white scale-50" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <p className="font-black text-slate-800 text-sm">NF #{inv.numero}</p>
                              <span className="text-[10px] text-slate-500 uppercase font-bold">{inv.tipo}</span>
                              {inv.status && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase font-bold">{inv.status}</span>}
                              {alreadyLinked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold">JA VINCULADA</span>}
                            </div>
                            <p className="text-xs text-slate-600 truncate">{inv.customer_name || '—'} {inv.customer_doc && <span className="text-slate-400 font-mono ml-1">{inv.customer_doc}</span>}</p>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {chaveMatch && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">✓ CHAVE IGUAL</span>}
                              {valueMatch && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">✓ VALOR IGUAL</span>}
                              {docMatch && <span className="text-[9px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">✓ CNPJ IGUAL</span>}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`font-black text-sm ${valueMatch ? 'text-emerald-700' : 'text-slate-700'}`}>{fmtBRL(inv.total)}</p>
                            <p className="text-[10px] text-slate-400">{fmtDate(inv.created_at)}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <p className="text-[10px] text-slate-500">
                Dica: NFs marcadas com <span className="font-bold text-emerald-700">CHAVE IGUAL</span>, <span className="font-bold text-emerald-700">VALOR IGUAL</span> ou <span className="font-bold text-blue-700">CNPJ IGUAL</span> tem alta probabilidade de ser o par correto. Ao vincular, o DF-e sera marcado como recebido e a chave de acesso sera gravada na NF.
              </p>
            </div>
            <div className="border-t border-slate-200 px-5 py-3 flex gap-2 justify-end bg-slate-50">
              <button onClick={() => setLinkModal(null)} className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm font-bold" data-testid="manifesto-link-cancel">Cancelar</button>
              <button
                onClick={doLink}
                disabled={!linkSelected || linking}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm flex items-center gap-2 shadow-md shadow-blue-500/30"
                data-testid="manifesto-link-confirm"
              >
                {linking && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                <Link2 size={14} /> Vincular
              </button>
            </div>
          </div>
        </div>
      )}
      {fetchChaveOpen && <ManifestoFetchChaveModal companyId={companyId} onClose={() => setFetchChaveOpen(false)} onDone={() => load()} />}
    </div>
  );
};

export default ManifestoPage;
